import type { RagResultToolDef } from '@kanbu/schema/contracts';
import { ContentType } from '@kanbu/schema/enums';
import { markdownCodeRanges } from '@kanbu/shared';

export type RagSource = NonNullable<RagResultToolDef['sources']>[number];

export function getHref(
  url: string | undefined,
  pages: string | undefined,
  mimeType?: string,
) {
  if (!url || !pages) {
    return url;
  }

  try {
    const parsedUrl = new URL(url);
    const [page] = parsePages(pages);

    if (
      page &&
      (mimeType === 'application/pdf' ||
        parsedUrl.pathname.toLowerCase().endsWith('.pdf'))
    ) {
      parsedUrl.hash = `page=${page}`;

      return parsedUrl.toString();
    }

    return url;
  } catch {
    return url;
  }
}

function isBareHostnameLabel(fileName: string | undefined, hostname: string) {
  const fn = (fileName ?? '').trim().toLowerCase();

  if (!fn) {
    return true;
  }

  const host = hostname.toLowerCase();
  const hostNoWww = host.replace(/^www\./, '');
  const fnNoWww = fn.replace(/^www\./, '');

  return (
    fn === host || fn === hostNoWww || fnNoWww === hostNoWww || fnNoWww === host
  );
}

function websiteOrExternalDisplayLabel(
  url: string,
  fileName: string | undefined,
) {
  const u = new URL(url);
  const path = u.pathname.replace(/\/$/, '') || '';
  const pathPart = path && path !== '/' ? path : '';
  const fromUrl = `${u.hostname}${pathPart}${u.search}${u.hash}`;
  const fn = (fileName ?? '').trim();
  const labelHasPath = fn.includes('/');
  const pathPrefersUrl =
    pathPart && (!fn || isBareHostnameLabel(fn, u.hostname) || !labelHasPath);
  const rootPrefersUrl = !pathPart && isBareHostnameLabel(fn, u.hostname);
  const useUrl = pathPrefersUrl || rootPrefersUrl;

  if (useUrl) {
    return fromUrl;
  }

  return fn || fromUrl;
}

export function getSourceDisplayLabel(source: RagSource): string {
  const { type, url, fileName } = source;

  if (type !== ContentType.Website && type !== ContentType.ExternalDocument) {
    return fileName ?? 'Untitled';
  }

  if (!url) {
    return fileName ?? 'Untitled';
  }

  try {
    return websiteOrExternalDisplayLabel(url, fileName);
  } catch {
    return fileName ?? 'Untitled';
  }
}

export function sourceReactKey(source: RagSource, index: number): string {
  return `${source.url || source.fileName}#${source.page ?? ''}#${index}`;
}

/** Parses a stored page list ("2,3,4") into sorted, unique page numbers. */
function parsePages(page: string | undefined): number[] {
  if (!page) {
    return [];
  }

  const numbers = page
    .split(',')
    .map(part => Number(part.trim()))
    .filter(value => Number.isSafeInteger(value) && value > 0);

  return [...new Set(numbers)].sort((a, b) => a - b);
}

/** Sorted page numbers as compact ranges: [2,5,6,7,10] → "2, 5–7, 10". */
function formatPageRanges(pages: number[]): string {
  if (pages.length === 0) {
    return '';
  }

  const groups: string[] = [];
  let start = pages[0]!;
  let prev = pages[0]!;

  for (const page of pages.slice(1)) {
    if (page === prev + 1) {
      prev = page;
      continue;
    }

    groups.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = page;
    prev = page;
  }

  groups.push(start === prev ? `${start}` : `${start}–${prev}`);

  return groups.join(', ');
}

/** Merges two stored page lists into one sorted, unique comma list. */
function combinePages(a: string | undefined, b: string | undefined): string {
  return [...new Set([...parsePages(a), ...parsePages(b)])]
    .sort((x, y) => x - y)
    .join(',');
}

/**
 * Compact page label from the stored page list. The full list drives the
 * PDF anchor (`#page=<first>`); the display shows contiguous ranges — "2",
 * "2–4", or "2, 5–7" once several pages of one document are merged.
 */
export function formatPageLabel(
  page: string | undefined,
  type?: ContentType,
): string {
  if (type === ContentType.Website) {
    return '';
  }

  return formatPageRanges(parsePages(page));
}

/**
 * Collapses sources for display: citations of the SAME document (matched by
 * `documentId`, else url, else label) merge into ONE entry — their pages are
 * combined into a single range list and their alias ids unioned — so one
 * document cited across several pages shows once (e.g. "Řád … · p. 2, 5–7")
 * instead of a chip per page. Url-less internal docs are kept.
 */
export function filterDisplaySources(
  sources: RagResultToolDef['sources'],
): RagSource[] {
  if (!sources?.length) {
    return [];
  }

  const byKey = new Map<string, RagSource>();
  const order: string[] = [];

  for (const source of sources) {
    const key = source.documentId || source.url || source.fileName || '';
    const incomingIds = source.ids?.length
      ? source.ids
      : source.id
        ? [source.id]
        : [];
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, { ...source, ids: [...incomingIds] });
      order.push(key);
      continue;
    }

    existing.page = combinePages(existing.page, source.page);
    existing.mimeType ??= source.mimeType;
    existing.ids ??= [];

    for (const id of incomingIds) {
      if (!existing.ids.includes(id)) {
        existing.ids.push(id);
      }
    }
  }

  return order.map(key => byKey.get(key)!);
}

export const CITATION_MARKER_RE = /\[(c\d+(?:\s*,\s*c\d+)*)]/gi;

export function parseCitationMarkerIds(group: string): string[] {
  return group
    .split(',')
    .map(part => part.trim())
    .filter(part => /^c\d+$/.test(part));
}

function transformProse(
  text: string,
  transform: (prose: string) => string,
): string {
  let result = '';
  let cursor = 0;
  for (const range of markdownCodeRanges(text)) {
    result +=
      transform(text.slice(cursor, range.start)) +
      text.slice(range.start, range.end);
    cursor = range.end;
  }

  return result + transform(text.slice(cursor));
}

export function preprocessCitationMarkers(text: string): string {
  return transformProse(text, prose =>
    prose.replaceAll(CITATION_MARKER_RE, (_, group: string) =>
      parseCitationMarkerIds(group)
        .map(id => `<sup data-cite="${id}"></sup>`)
        .join(''),
    ),
  );
}

/**
 * Removes inline `[cN]` citation markers from the answer text — used for the
 * default "sources list" display, where provenance is shown as a list at the
 * end of the answer rather than as per-claim inline chips. Collapses the
 * whitespace/punctuation left behind so sentences read cleanly.
 */
export function stripCitationMarkers(text: string): string {
  return transformProse(text, prose =>
    prose
      .replaceAll(CITATION_MARKER_RE, '')
      .replaceAll(/ +([!,.:;?])/g, '$1')
      .replaceAll(/[\t ]{2,}/g, ' '),
  );
}

/** Citation ids in first-appearance order across the answer text. */
export function collectMarkerIdsInOrder(text: string): string[] {
  const ids: string[] = [];

  transformProse(text, prose => {
    for (const match of prose.matchAll(CITATION_MARKER_RE)) {
      ids.push(...parseCitationMarkerIds(match[1] ?? ''));
    }

    return prose;
  });

  return ids;
}

export interface CitationEntry {
  /** 1-based number rendered as the inline superscript. */
  number: number;
  /** Resolved source, absent while the answer is still streaming. */
  source?: RagSource;
}

/**
 * Maps every inline citation id to a stable number + its source.
 *
 * Numbering follows the sources array order (first-citation order, which
 * `resolveCitedSources` preserves), and every alias of a deduped source
 * shares that source's number. While streaming — before `sources` arrive —
 * ids are numbered by first appearance in the text so placeholders stay
 * consistent, then hydrate into links once sources land.
 */
export function buildCitationNumbering(
  text: string,
  sources: RagResultToolDef['sources'],
): Map<string, CitationEntry> {
  const map = new Map<string, CitationEntry>();
  const display = filterDisplaySources(sources);

  display.forEach((source, index) => {
    const aliasIds = source.ids?.length
      ? source.ids
      : source.id
        ? [source.id]
        : [];

    for (const id of aliasIds) {
      map.set(id.trim(), { number: index + 1, source });
    }
  });

  let next = display.length + 1;

  for (const id of collectMarkerIdsInOrder(text)) {
    if (!map.has(id)) {
      map.set(id, { number: next });
      next += 1;
    }
  }

  return map;
}

export function findSourceByCitationId(
  id: string,
  sources: RagResultToolDef['sources'],
): RagSource | undefined {
  if (!sources?.length) {
    return undefined;
  }

  const normalized = id.trim();

  return filterDisplaySources(sources).find(source =>
    source.ids?.length
      ? source.ids.includes(normalized)
      : source.id === normalized,
  );
}
